/**
 * Задачи и их содержимое.
 *
 * Перемещение — единственная мутация с оптимистичным обновлением: карточка
 * должна двигаться мгновенно, иначе перетаскивание ощущается сломанным.
 * При отказе сервера изменение откатывается (§13.5).
 */

import { api } from "../api";
import type { KanbanCardData } from "../boards/kanbanTypes";
import type { RichDoc } from "../richtext";

import { applyMove } from "./applyMove";

export interface TaskListItem extends KanbanCardData {
  boardId: string;
  version: number;
  /** Только в списке без доски (задачи подразделения): где задача живёт. */
  boardName?: string | null;
  /** Системный статус, а без него — название столбца. */
  statusLabel?: string | null;
  projectName?: string | null;
}

export interface TaskFilters {
  /** Без доски — задачи всех видимых досок; сужаются подразделением. */
  boardId?: string;
  departmentId?: string;
  search?: string;
  assigneeIds?: string[];
  priorityIds?: string[];
  typeIds?: string[];
  tagIds?: string[];
  dueFrom?: string;
  dueTo?: string;
  /** Период работы — как в «Отчёте по работам»: выполнены в нём или были
   *  в работе; срок не важен (список задач подразделения). */
  workFrom?: string;
  workTo?: string;
  overdueOnly?: boolean;
  mineOnly?: boolean;
  /** Работа окончена (DONE) или ещё идёт (OPEN); без поля — все задачи. */
  state?: "OPEN" | "DONE";
  /** Срок сегодня или прошёл, работа не окончена — для «Мои сегодня». */
  dueTodayOrOverdue?: boolean;
}

export const tasksApi = api.injectEndpoints({
  endpoints: (build) => ({
    tasks: build.query<{ data: TaskListItem[] }, TaskFilters>({
      query: (params) => ({ method: "tasks.list", params }),
      providesTags: ["TaskList"],
    }),

    moveTask: build.mutation<
      { key: string; columnId: string; version: number },
      {
        key: string;
        toColumnId: string;
        position: number;
        afterTaskKey: string | null;
        version: number;
        boardId: string;
      }
    >({
      query: ({ boardId: _boardId, ...params }) => ({ method: "tasks.move", params }),
      // Список правится оптимистично, а перенос попадает в историю задачи.
      invalidatesTags: ["Events"],

      // lifecycleApi берётся целиком, а не деструктурируется: getState —
      // метод, и в отрыве от объекта он теряет привязку this.
      async onQueryStarted(args, lifecycleApi) {
        // Оптимистичное перемещение во всех кэшах списка этой доски.
        const patches = tasksApi.util
          .selectInvalidatedBy(lifecycleApi.getState(), ["TaskList"])
          .map((entry) =>
            lifecycleApi.dispatch(
              tasksApi.util.updateQueryData(
                "tasks",
                entry.originalArgs as TaskFilters,
                (draft) => {
                  // Не только столбец, но и место в нём (§12.1).
                  applyMove(draft.data, {
                    taskKey: args.key,
                    toColumnId: args.toColumnId,
                    position: args.position,
                    afterTaskKey: args.afterTaskKey,
                  });
                },
              ),
            ),
          );

        try {
          const { data } = await lifecycleApi.queryFulfilled;
          // Сервер повышает версию при каждом переносе. Прежде в кэше
          // оставалась старая: следующий перенос той же карточки уходил
          // с ней, получал 409 и откатывался — перенести задачу туда
          // и обратно без перезагрузки страницы было нельзя.
          for (const entry of tasksApi.util.selectInvalidatedBy(lifecycleApi.getState(), [
            "TaskList",
          ])) {
            lifecycleApi.dispatch(
              tasksApi.util.updateQueryData("tasks", entry.originalArgs as TaskFilters, (draft) => {
                const task = draft.data.find((item) => item.key === data.key);
                if (task) {
                  task.columnId = data.columnId;
                  task.version = data.version;
                }
              }),
            );
          }
        } catch {
          // §13.5: молчаливая потеря изменения недопустима — карточка
          // возвращается на место, а сообщение показывает вызывающий код.
          for (const patch of patches) patch.undo();
        }
      },
    }),
  }),
});

export const { useTasksQuery, useMoveTaskMutation } = tasksApi;

// ─── Карточка задачи (§11.7, §13, §14) ──────────────────────────────────

/** Наблюдатель — та же короткая форма человека, что у исполнителя на карточке. */
export interface TaskWatcher {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface TaskAssignee {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  position: string | null;
  avatarUrl: string | null;
}

export interface Attachment {
  id: string;
  filename: string;
  sizeLabel: string;
  mime: string;
  /** Видео записи бага — удаляется при закрытии задачи. У файлов комментариев не бывает. */
  isRecording?: boolean;
  uploadedByName: string;
  uploadedAtLabel: string;
  canDelete: boolean;
}

export interface TaskDetail {
  key: string;
  title: string;
  /** Обычный текст описания — выводится сервером из descriptionDoc. */
  description: string | null;
  /** Описание с оформлением — источник истины. У старых задач — абзацы из текста. */
  descriptionDoc?: RichDoc | null;
  boardId: string;
  boardName: string;
  /** Задача на личной доске: проекта, постановщика, исполнителей,
   *  наблюдателей и автора в карточке нет — у неё один человек. */
  boardIsPersonal?: boolean;
  columnId: string;
  columnName: string;
  /** Разрешённый резолвером проект: с доски или со столбца (§2.4). */
  projectName: string | null;
  statusTag: { code: string; label: string } | null;
  priorityId: string | null;
  priorityName: string | null;
  priorityColor: string | null;
  typeId: string | null;
  typeName: string | null;
  dueDate: string | null;
  overdue: boolean;
  /** Напоминание исполнителям: момент ISO 8601 с поясом; null — не задано. */
  remindAt?: string | null;
  reporterId: string | null;
  reporterName: string | null;
  creatorName: string;
  createdAtLabel: string;
  updatedAtLabel: string;
  assignees: TaskAssignee[];
  /** Наблюдатели: получают уведомления по задаче, не отвечая за неё. */
  watchers: TaskWatcher[];
  tags: { id: string; name: string; color: string | null }[];
  commentCount: number;
  /** Вложения приходят вместе с задачей: отдельного эндпоинта списка
   *  в §21.7 нет, и добавлять его в контракт ради удобства нельзя. */
  attachments: Attachment[];
  linkCount: number;
  archived: boolean;
  /** Личная отметка о просмотре смотрящего — действует до 00:00. */
  viewed: boolean;
  /** Время отметки «ЧЧ:ММ» — для подсказки. */
  viewedAtLabel: string | null;
  version: number;
}

export interface TaskLink {
  id: string;
  type: string;
  typeLabel: string;
  partnerKey: string;
  /** Доска связанной задачи — она бывает и не той, что открыта. */
  partnerBoardId: string;
  partnerTitle: string;
  partnerColumnName: string;
  partnerArchived: boolean;
  /** Связанная задача выполнена — по её статусу, а не по названию столбца. */
  partnerDone: boolean;
}

export interface TaskEventItem {
  id: string;
  type: string;
  actorName: string;
  occurredAtLabel: string;
  /** Готовое человекочитаемое описание — формируется сервером,
   *  чтобы история в интерфейсе и в аудите совпадала дословно. */
  summary: string;
  changes?: { field: string; before: string | null; after: string | null }[];
}

export interface CommentItem {
  /** Personal acknowledgement, independent of the notification's read status. */
  isUnread?: boolean;
  id: string;
  parentCommentId: string | null;
  authorId: string;
  authorFirstName: string;
  authorLastName: string;
  authorAvatarUrl: string | null;
  /** Обычный текст — выводится сервером из body. */
  text: string;
  /** Текст с оформлением; у комментариев до редактора — абзацы из text. */
  body?: RichDoc | null;
  createdAtLabel: string;
  editedLabel: string | null;
  canEdit: boolean;
  canDelete: boolean;
  attachments: Attachment[];
  /** §22.2: правка комментария идёт с версией, и после каждой она меняется. */
  version: number;
}

export const taskDetailApi = api.injectEndpoints({
  endpoints: (build) => ({
    task: build.query<TaskDetail, string>({
      query: (key) => ({ method: "tasks.get", params: { key } }),
      providesTags: (_r, _e, key) => [{ type: "Task" as const, id: key }, "Task"],
    }),

    createTask: build.mutation<
      TaskDetail,
      {
        boardId: string;
        columnId: string;
        title: string;
        /** Описание с оформлением; null — без описания. */
        descriptionDoc: RichDoc | null;
        typeId: string;
        priorityId: string;
        dueDate: string | null;
              assigneeIds: string[];
        tagIds: string[];
        reporterId: string | null;
        /** Только доска «проект на столбце»; без него — «Запланировано». */
        statusTagId?: string;
        clientRequestId: string;
      }
    >({
      // §11.4: после создания сбрасываются доска, её счётчики и отчётность.
      query: (params) => ({ method: "tasks.create", params }),
      invalidatesTags: ["TaskList", "Board", "BoardList", "Report"],
    }),

    updateTask: build.mutation<
      TaskDetail,
      {
        key: string;
        version: number;
        title?: string;
        /** Документ редактора; null — очистить описание. */
        descriptionDoc?: RichDoc | null;
        priorityId?: string | null;
        typeId?: string | null;
        dueDate?: string | null;
        /** Момент напоминания исполнителям (ISO с поясом); null — снять. */
        remindAt?: string | null;
        /** §11.3: постановщика меняет любой участник доски с правом правки. */
        reporterId?: string | null;
      }
    >({
      query: (params) => ({ method: "tasks.update", params }),
      invalidatesTags: ["Task", "TaskList", "Board", "Events"],
    }),

    setAssignees: build.mutation<
      TaskDetail,
      { key: string; version: number; userIds: string[] }
    >({
      // §11.5: передаётся полный набор, а не «добавить/убрать» —
      // так параллельные правки не складываются в неверный список.
      query: (params) => ({ method: "tasks.setAssignees", params }),
      invalidatesTags: ["Task", "TaskList", "Board", "Events"],
    }),

    // Полный набор, но без версии: наблюдатели не меняют полей задачи,
    // и правка их состава не должна давать соседу по карточке 409.
    // Себя добавляет любой, кто видит задачу; других — кто назначает.
    setWatchers: build.mutation<{ key: string; watcherIds: string[] }, { key: string; userIds: string[] }>({
      query: (params) => ({ method: "tasks.setWatchers", params }),
      invalidatesTags: ["Task"],
    }),

    setTaskTags: build.mutation<TaskDetail, { key: string; version: number; tagIds: string[] }>({
      query: (params) => ({ method: "tasks.setTags", params }),
      invalidatesTags: ["Task", "TaskList", "Board", "Events"],
    }),

    setStatusTag: build.mutation<TaskDetail, { key: string; version: number; tagId: string }>({
      query: (params) => ({ method: "tasks.setStatusTag", params }),
      invalidatesTags: ["Task", "TaskList", "Board", "Events"],
    }),

    // Отметка одного пункта чек-листа из просмотра описания: без версии —
    // меняется только этот пункт, чужая правка текста не затирается.
    setChecklistItem: build.mutation<TaskDetail, { key: string; itemId: string; checked: boolean }>({
      query: (params) => ({ method: "tasks.setChecklistItem", params }),
      invalidatesTags: ["Task", "Events"],
    }),

    // Смена столбца из карточки задачи (доска «один проект на всю доску»).
    // Задача встаёт в начало столбца; доска и карточка перечитываются —
    // оптимистичная правка списка здесь не нужна, перенос не перетаскиванием.
    moveTaskToColumn: build.mutation<
      { key: string; columnId: string; version: number },
      { key: string; toColumnId: string; version: number }
    >({
      query: (params) => ({
        method: "tasks.move",
        params: { ...params, position: 0, afterTaskKey: null },
      }),
      invalidatesTags: ["Task", "TaskList", "Events"],
    }),

    // Отметка личная: задачу не меняет, поэтому версия не нужна.
    setTaskViewed: build.mutation<TaskDetail, { key: string; viewed: boolean }>({
      query: (params) => ({ method: "tasks.setViewed", params }),
      invalidatesTags: ["Task", "TaskList"],
    }),

    // Выгрузка списка задач подразделения: те же фильтры, что у списка,
    // плюс порядок строк экрана. Файл собирается в фоне.
    // Отчёт по работам отдела ОА: теги всех его досок — по названию.
    workReportTags: build.query<{ data: { name: string }[] }, void>({
      query: () => ({ method: "tasks.workReport.tags", params: {} }),
    }),

    exportWorkReport: build.mutation<
      { jobId: string; format: string; projects: number },
      {
        from: string;
        to: string;
        /** Без tag — с withoutTag: все задачи досок ОА за период. */
        tag?: string;
        withoutTag?: boolean;
        format: "XLSX" | "PDF";
        clientRequestId: string;
      }
    >({
      query: (params) => ({ method: "tasks.workReport.export", params }),
    }),

    exportTasks: build.mutation<
      { jobId: string; format: string },
      TaskFilters & {
        format: "XLSX" | "DOCX" | "PDF";
        keys: string[];
        title: string;
        filtersLabel: string;
        clientRequestId: string;
      }
    >({
      query: (params) => ({ method: "tasks.export", params }),
    }),

    taskLinks: build.query<{ data: TaskLink[] }, string>({
      query: (key) => ({ method: "tasks.links.list", params: { key } }),
      providesTags: ["Links"],
    }),

    // Кандидаты для связи: задачи подразделения, видимые пользователю (макет:
    // «Найти задачу (в этом отделе)»). Отдельный запрос, а не tasks с boardId:
    // связь может вести на другую доску того же отдела.
    taskSearch: build.query<{ data: TaskListItem[] }, { departmentId: string; search: string }>({
      query: (params) => ({ method: "tasks.list", params }),
      providesTags: ["TaskList"],
    }),

    createLink: build.mutation<
      TaskLink,
      { key: string; targetKey: string; type: string; clientRequestId: string }
    >({
      query: (params) => ({ method: "tasks.links.create", params }),
      invalidatesTags: ["Links", "Task", "Events"],
    }),

    deleteLink: build.mutation<{ ok: boolean }, { key: string; linkId: string }>({
      query: (params) => ({ method: "tasks.links.delete", params }),
      invalidatesTags: ["Links", "Task", "Events"],
    }),

    comments: build.query<{ data: CommentItem[] }, string>({
      query: (key) => ({ method: "comments.list", params: { key } }),
      providesTags: ["Comments"],
    }),

    markCommentsRead: build.mutation<{ ok: boolean }, { key: string; ids: string[] }>({
      query: (params) => ({ method: "comments.markRead", params }),
      async onQueryStarted({ key, ids }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const read = new Set(ids);
          dispatch(taskDetailApi.util.updateQueryData("comments", key, (draft) => {
            for (const item of draft.data) if (read.has(item.id)) item.isUnread = false;
          }));
        } catch { /* Retry on the next successful comments refresh. */ }
      },
    }),

    createComment: build.mutation<
      CommentItem,
      {
        key: string;
        text: string;
        parentCommentId: string | null;
        clientRequestId: string;
        /** Текст с оформлением — источник истины; text сервер выводит из него. */
        body?: RichDoc | null;
        /** Следом прикладываются файлы — тогда текст может быть пустым. */
        withFiles?: boolean;
      }
    >({
      query: (params) => ({ method: "comments.create", params }),
      // TaskList — счётчик комментариев на карточке доски.
      invalidatesTags: ["Comments", "Task", "TaskList", "Events"],
    }),

    updateComment: build.mutation<
      CommentItem,
      { id: string; text: string; body?: RichDoc | null; version: number }
    >({
      query: (params) => ({ method: "comments.update", params }),
      invalidatesTags: ["Comments", "Events"],
    }),

    deleteComment: build.mutation<{ ok: boolean }, { id: string }>({
      query: (params) => ({ method: "comments.delete", params }),
      invalidatesTags: ["Comments", "Task", "TaskList", "Events"],
    }),

    // Загрузка в три шага: ссылка → байты по HTTP → подтверждение (§14.2).
    requestAttachmentUpload: build.mutation<
      { attachmentId: string; uploadUrl: string; maxBytes: number },
      {
        key: string;
        filename: string;
        mime: string;
        size: number;
        commentId?: string;
        /** Видео записи бага: сервер удалит его при закрытии задачи. */
        recording?: boolean;
      }
    >({
      query: (params) => ({ method: "attachments.requestUpload", params }),
    }),

    completeAttachment: build.mutation<{ id: string; filename: string }, { id: string }>({
      query: (params) => ({ method: "attachments.complete", params }),
      invalidatesTags: ["Task", "Attachments", "TaskList", "Comments", "Events"],
    }),

    // Ссылка на просмотр: картинки и PDF сервер отдаёт inline. Мутация — по той же
    // причине, что и скачивание.
    attachmentOpenUrl: build.mutation<
      { url: string; filename: string; expiresIn: number },
      { id: string }
    >({
      query: (params) => ({ method: "attachments.openUrl", params }),
    }),

    // Мутация, а не запрос: подписанная ссылка живёт минуты, кэшировать её нельзя.
    attachmentDownloadUrl: build.mutation<
      { url: string; filename: string; expiresIn: number },
      { id: string }
    >({
      query: (params) => ({ method: "attachments.downloadUrl", params }),
    }),

    deleteAttachment: build.mutation<{ ok: boolean }, { id: string }>({
      query: (params) => ({ method: "attachments.delete", params }),
      // Файл мог висеть на комментарии — обсуждение тоже перечитывается.
      invalidatesTags: ["Attachments", "Comments", "Task", "Events"],
    }),

    taskEvents: build.query<{ data: TaskEventItem[] }, string>({
      query: (key) => ({ method: "tasks.events.list", params: { key } }),
      providesTags: ["Events"],
    }),
  }),
});

export const {
  useTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useSetAssigneesMutation,
  useSetWatchersMutation,
  useSetTaskTagsMutation,
  useSetStatusTagMutation,
  useSetTaskViewedMutation,
  useMoveTaskToColumnMutation,
  useSetChecklistItemMutation,
  useExportTasksMutation,
  useWorkReportTagsQuery,
  useExportWorkReportMutation,
  useTaskLinksQuery,
  useCreateLinkMutation,
  useDeleteLinkMutation,
  useCommentsQuery,
  useMarkCommentsReadMutation,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useDeleteAttachmentMutation,
  useTaskSearchQuery,
  useRequestAttachmentUploadMutation,
  useCompleteAttachmentMutation,
  useAttachmentOpenUrlMutation,
  useAttachmentDownloadUrlMutation,
  useTaskEventsQuery,
} = taskDetailApi;

// ─── Архив (§15) ─────────────────────────────────────────────────────────

export interface ArchivedTask {
  key: string;
  title: string;
  boardId: string;
  /** Доска и столбец на момент архивации — §15.5 восстанавливает именно туда. */
  sourceBoardName: string;
  sourceColumnName: string;
  projectName: string | null;
  priorityName: string | null;
  typeName: string | null;
  dueDate: string | null;
  assignees: { id: string; firstName: string; lastName: string; avatarUrl: string | null }[];
  /** ISO — для сортировки; подпись для показа — archivedAtLabel. */
  archivedAt: string | null;
  archivedAtLabel: string;
  archivedByName: string;
  reason: string | null;
  /** Столбец мог быть удалён после архивации — восстанавливать некуда. */
  canRestore: boolean;
  version: number;
}

export const archiveApi = api.injectEndpoints({
  endpoints: (build) => ({
    archivedTasks: build.query<
      { data: ArchivedTask[] },
      {
        boardId: string;
        search?: string;
        assigneeIds?: string[];
        priorityIds?: string[];
        typeIds?: string[];
        tagIds?: string[];
        dueFrom?: string;
        dueTo?: string;
        mineOnly?: boolean;
      }
    >({
      // Архив — отдельное состояние задачи, а не последний столбец канбана (§0.3).
      query: (params) => ({ method: "tasks.list", params: { ...params, archived: true } }),
      providesTags: ["Archive"],
    }),

    archiveTask: build.mutation<
      { ok: boolean },
      { key: string; version: number; reason: string }
    >({
      query: (params) => ({ method: "tasks.archive", params }),
      invalidatesTags: ["Archive", "TaskList", "Board", "Task", "Events"],
    }),

    // Навсегда, без архива: вместе с комментариями, файлами и историей.
    deleteTask: build.mutation<{ key: string; deleted: boolean }, { key: string; version: number }>({
      query: (params) => ({ method: "tasks.delete", params }),
      invalidatesTags: ["Archive", "TaskList", "Board", "Events"],
    }),

    // Все задачи столбца «Закрыто» доски с одним проектом — одним вызовом:
    // сервер архивирует их в одной транзакции.
    archiveColumnTasks: build.mutation<
      { archived: number; keys: string[] },
      { boardId: string; columnId: string; reason: string }
    >({
      query: (params) => ({ method: "tasks.archiveColumn", params }),
      invalidatesTags: ["Archive", "TaskList", "Board", "Task", "Events"],
    }),

    restoreTask: build.mutation<
      { key: string; columnId: string },
      { key: string; targetColumnId?: string }
    >({
      query: (params) => ({ method: "tasks.restore", params }),
      invalidatesTags: ["Archive", "TaskList", "Board", "Task", "Events"],
    }),
  }),
});

export const {
  useArchivedTasksQuery,
  useArchiveTaskMutation,
  useDeleteTaskMutation,
  useArchiveColumnTasksMutation,
  useRestoreTaskMutation,
} = archiveApi;
